import {
  LID_TYPES,
  nextAvailableLidControlName,
  type LidControl,
  type LidDrain,
  type LidDrainMat,
  type LidPavement,
  type LidRemoval,
  type LidSoil,
  type LidStorage,
  type LidSurface,
  type LidType,
} from "../model/inp/lidControls.ts";
import type { Pollutant } from "../model/inp/pollutants.ts";
import type { TextKey } from "../i18n/en.ts";
import { useT } from "../i18n/hooks.ts";
import { TrashIcon } from "./TrashIcon.tsx";
import { PlusIcon } from "./PlusIcon.tsx";
import { IconButton } from "./IconButton.tsx";
import { LayerSection, NumberField, NumberInput } from "./fields.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";

const SURFACE_DEFAULTS: LidSurface = {
  storageHeight: 0,
  vegVolFrac: 0,
  roughness: 0,
  surfSlope: 0,
  sideSlope: 0,
};
const SOIL_DEFAULTS: LidSoil = {
  thickness: 0,
  porosity: 0,
  fieldCap: 0,
  wiltPoint: 0,
  kSat: 0,
  kSlope: 0,
  suction: 0,
};
const STORAGE_DEFAULTS: LidStorage = {
  thickness: 0,
  voidRatio: 0,
  kSat: 0,
  clogFactor: 0,
  covered: false,
};
const PAVEMENT_DEFAULTS: LidPavement = {
  thickness: 0,
  voidRatio: 0,
  impervFrac: 0,
  permeability: 0,
  clogFactor: 0,
  regenDays: 0,
  regenDegree: 0,
};
const DRAIN_DEFAULTS: LidDrain = {
  coeff: 0,
  expon: 0,
  offset: 0,
  delay: 0,
  hOpen: 0,
  hClose: 0,
  curve: "",
};
const DRAIN_MAT_DEFAULTS: LidDrainMat = { thickness: 0, voidRatio: 0, roughness: 0 };

const LID_TYPE_LABEL_KEYS: Record<LidType, TextKey> = {
  BC: "lid.typeBC",
  RG: "lid.typeRG",
  GR: "lid.typeGR",
  IT: "lid.typeIT",
  PP: "lid.typePP",
  RB: "lid.typeRB",
  VS: "lid.typeVS",
  RD: "lid.typeRD",
};

/**
 * Each control's detail is up to 6 optional layers, each with its own
 * presence checkbox: checking it creates the layer with all-zero defaults,
 * unchecking sets it back to null (dropping it from the file on save).
 */
export function LidControlsEditor({
  controls,
  onChange,
  selectedName,
  onSelect,
  pollutants,
}: {
  controls: LidControl[];
  onChange: (controls: LidControl[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
  pollutants: Pollutant[];
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deleteControl,
    rename: renameSelected,
    update,
  } = useNamedCollection(controls, onChange, selectedName, onSelect);

  function addControl() {
    const name = nextAvailableLidControlName(new Set(controls.map((c) => c.name)));
    add({
      name,
      type: "BC",
      surface: null,
      soil: null,
      storage: null,
      pavement: null,
      drain: null,
      drainMat: null,
      removals: [],
    });
  }

  function patch(fields: Partial<LidControl>) {
    update((c) => ({ ...c, ...fields }));
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={controls}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addControl}
        addLabel={t("lid.addControl")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("lid.noControlSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deleteControl(selected.name)}
              deleteLabel={t("lid.deleteControl")}
            >
              <label className="field-pill">
                <span className="field-pill-label">{t("lid.type")}</span>
                <select
                  value={selected.type}
                  onChange={(e) => patch({ type: e.target.value as LidType })}
                >
                  {LID_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(LID_TYPE_LABEL_KEYS[type])}
                    </option>
                  ))}
                </select>
              </label>
            </NamedCollectionHeader>

            <LayerSection
              title={t("lid.layerSurface")}
              enabled={selected.surface !== null}
              onToggle={(on) => patch({ surface: on ? { ...SURFACE_DEFAULTS } : null })}
            >
              {selected.surface && (
                <div className="named-collection-fields">
                  <NumberField
                    label={t("lid.surfaceStorageHeight")}
                    value={selected.surface.storageHeight}
                    onChange={(v) => patch({ surface: { ...selected.surface!, storageHeight: v } })}
                  />
                  <NumberField
                    label={t("lid.surfaceVegFraction")}
                    value={selected.surface.vegVolFrac}
                    onChange={(v) => patch({ surface: { ...selected.surface!, vegVolFrac: v } })}
                  />
                  <NumberField
                    label={t("lid.surfaceRoughness")}
                    value={selected.surface.roughness}
                    onChange={(v) => patch({ surface: { ...selected.surface!, roughness: v } })}
                  />
                  <NumberField
                    label={t("lid.surfaceSlope")}
                    value={selected.surface.surfSlope}
                    onChange={(v) => patch({ surface: { ...selected.surface!, surfSlope: v } })}
                  />
                  <NumberField
                    label={t("lid.surfaceSideSlope")}
                    value={selected.surface.sideSlope}
                    onChange={(v) => patch({ surface: { ...selected.surface!, sideSlope: v } })}
                  />
                </div>
              )}
            </LayerSection>

            <LayerSection
              title={t("lid.layerSoil")}
              enabled={selected.soil !== null}
              onToggle={(on) => patch({ soil: on ? { ...SOIL_DEFAULTS } : null })}
            >
              {selected.soil && (
                <div className="named-collection-fields">
                  <NumberField
                    label={t("lid.soilThickness")}
                    value={selected.soil.thickness}
                    onChange={(v) => patch({ soil: { ...selected.soil!, thickness: v } })}
                  />
                  <NumberField
                    label={t("lid.soilPorosity")}
                    value={selected.soil.porosity}
                    onChange={(v) => patch({ soil: { ...selected.soil!, porosity: v } })}
                  />
                  <NumberField
                    label={t("lid.soilFieldCap")}
                    value={selected.soil.fieldCap}
                    onChange={(v) => patch({ soil: { ...selected.soil!, fieldCap: v } })}
                  />
                  <NumberField
                    label={t("lid.soilWiltPoint")}
                    value={selected.soil.wiltPoint}
                    onChange={(v) => patch({ soil: { ...selected.soil!, wiltPoint: v } })}
                  />
                  <NumberField
                    label={t("lid.soilKSat")}
                    value={selected.soil.kSat}
                    onChange={(v) => patch({ soil: { ...selected.soil!, kSat: v } })}
                  />
                  <NumberField
                    label={t("lid.soilKSlope")}
                    value={selected.soil.kSlope}
                    onChange={(v) => patch({ soil: { ...selected.soil!, kSlope: v } })}
                  />
                  <NumberField
                    label={t("lid.soilSuction")}
                    value={selected.soil.suction}
                    onChange={(v) => patch({ soil: { ...selected.soil!, suction: v } })}
                  />
                </div>
              )}
            </LayerSection>

            <LayerSection
              title={t("lid.layerStorage")}
              enabled={selected.storage !== null}
              onToggle={(on) => patch({ storage: on ? { ...STORAGE_DEFAULTS } : null })}
            >
              {selected.storage && (
                <div className="named-collection-fields">
                  <NumberField
                    label={t("lid.storageThickness")}
                    value={selected.storage.thickness}
                    onChange={(v) => patch({ storage: { ...selected.storage!, thickness: v } })}
                  />
                  <NumberField
                    label={t("lid.storageVoidRatio")}
                    value={selected.storage.voidRatio}
                    onChange={(v) => patch({ storage: { ...selected.storage!, voidRatio: v } })}
                  />
                  <NumberField
                    label={t("lid.storageKSat")}
                    value={selected.storage.kSat}
                    onChange={(v) => patch({ storage: { ...selected.storage!, kSat: v } })}
                  />
                  <NumberField
                    label={t("lid.storageClogFactor")}
                    value={selected.storage.clogFactor}
                    onChange={(v) => patch({ storage: { ...selected.storage!, clogFactor: v } })}
                  />
                  <label className="options-field options-field-checkbox">
                    <input
                      type="checkbox"
                      checked={selected.storage.covered}
                      onChange={(e) =>
                        patch({ storage: { ...selected.storage!, covered: e.target.checked } })
                      }
                    />
                    {t("lid.storageCovered")}
                  </label>
                </div>
              )}
            </LayerSection>

            <LayerSection
              title={t("lid.layerPavement")}
              enabled={selected.pavement !== null}
              onToggle={(on) => patch({ pavement: on ? { ...PAVEMENT_DEFAULTS } : null })}
            >
              {selected.pavement && (
                <div className="named-collection-fields">
                  <NumberField
                    label={t("lid.pavementThickness")}
                    value={selected.pavement.thickness}
                    onChange={(v) => patch({ pavement: { ...selected.pavement!, thickness: v } })}
                  />
                  <NumberField
                    label={t("lid.pavementVoidRatio")}
                    value={selected.pavement.voidRatio}
                    onChange={(v) => patch({ pavement: { ...selected.pavement!, voidRatio: v } })}
                  />
                  <NumberField
                    label={t("lid.pavementImpervFrac")}
                    value={selected.pavement.impervFrac}
                    onChange={(v) => patch({ pavement: { ...selected.pavement!, impervFrac: v } })}
                  />
                  <NumberField
                    label={t("lid.pavementPermeability")}
                    value={selected.pavement.permeability}
                    onChange={(v) =>
                      patch({ pavement: { ...selected.pavement!, permeability: v } })
                    }
                  />
                  <NumberField
                    label={t("lid.pavementClogFactor")}
                    value={selected.pavement.clogFactor}
                    onChange={(v) => patch({ pavement: { ...selected.pavement!, clogFactor: v } })}
                  />
                  <NumberField
                    label={t("lid.pavementRegenDays")}
                    value={selected.pavement.regenDays}
                    onChange={(v) => patch({ pavement: { ...selected.pavement!, regenDays: v } })}
                  />
                  <NumberField
                    label={t("lid.pavementRegenDegree")}
                    value={selected.pavement.regenDegree}
                    onChange={(v) => patch({ pavement: { ...selected.pavement!, regenDegree: v } })}
                  />
                </div>
              )}
            </LayerSection>

            <LayerSection
              title={t("lid.layerDrain")}
              enabled={selected.drain !== null}
              onToggle={(on) => patch({ drain: on ? { ...DRAIN_DEFAULTS } : null })}
            >
              {selected.drain && (
                <div className="named-collection-fields">
                  <NumberField
                    label={t("lid.drainCoeff")}
                    value={selected.drain.coeff}
                    onChange={(v) => patch({ drain: { ...selected.drain!, coeff: v } })}
                  />
                  <NumberField
                    label={t("lid.drainExpon")}
                    value={selected.drain.expon}
                    onChange={(v) => patch({ drain: { ...selected.drain!, expon: v } })}
                  />
                  <NumberField
                    label={t("lid.drainOffset")}
                    value={selected.drain.offset}
                    onChange={(v) => patch({ drain: { ...selected.drain!, offset: v } })}
                  />
                  <NumberField
                    label={t("lid.drainDelay")}
                    value={selected.drain.delay}
                    onChange={(v) => patch({ drain: { ...selected.drain!, delay: v } })}
                  />
                  <NumberField
                    label={t("lid.drainHOpen")}
                    value={selected.drain.hOpen}
                    onChange={(v) => patch({ drain: { ...selected.drain!, hOpen: v } })}
                  />
                  <NumberField
                    label={t("lid.drainHClose")}
                    value={selected.drain.hClose}
                    onChange={(v) => patch({ drain: { ...selected.drain!, hClose: v } })}
                  />
                  <label className="options-field">
                    {t("lid.drainCurve")}
                    <input
                      type="text"
                      value={selected.drain.curve}
                      onChange={(e) =>
                        patch({ drain: { ...selected.drain!, curve: e.target.value } })
                      }
                    />
                  </label>
                </div>
              )}
            </LayerSection>

            <LayerSection
              title={t("lid.layerDrainMat")}
              enabled={selected.drainMat !== null}
              onToggle={(on) => patch({ drainMat: on ? { ...DRAIN_MAT_DEFAULTS } : null })}
            >
              {selected.drainMat && (
                <div className="named-collection-fields">
                  <NumberField
                    label={t("lid.drainMatThickness")}
                    value={selected.drainMat.thickness}
                    onChange={(v) => patch({ drainMat: { ...selected.drainMat!, thickness: v } })}
                  />
                  <NumberField
                    label={t("lid.drainMatVoidRatio")}
                    value={selected.drainMat.voidRatio}
                    onChange={(v) => patch({ drainMat: { ...selected.drainMat!, voidRatio: v } })}
                  />
                  <NumberField
                    label={t("lid.drainMatRoughness")}
                    value={selected.drainMat.roughness}
                    onChange={(v) => patch({ drainMat: { ...selected.drainMat!, roughness: v } })}
                  />
                </div>
              )}
            </LayerSection>

            <RemovalsSection
              removals={selected.removals}
              pollutants={pollutants}
              onChange={(removals) => patch({ removals })}
            />
          </>
        )}
      </div>
    </div>
  );
}

function RemovalsSection({
  removals,
  pollutants,
  onChange,
}: {
  removals: LidRemoval[];
  pollutants: Pollutant[];
  onChange: (removals: LidRemoval[]) => void;
}) {
  const t = useT();
  const usedNames = new Set(removals.map((r) => r.pollutant));
  const availableToAdd = pollutants.filter((p) => !usedNames.has(p.name));

  function addRow() {
    if (availableToAdd.length === 0) return;
    onChange([...removals, { pollutant: availableToAdd[0].name, percent: 0 }]);
  }

  // Indexed by position, not by pollutant name — a `[LID_CONTROLS]`
  // REMOVALS row is never deduplicated on read (`lidControls.ts`'s own
  // `readLidControls`), so two rows naming the SAME pollutant is a real,
  // reachable state from a hand-edited/legacy `.inp`. Matching by name here
  // used to edit/delete every row sharing that name at once instead of just
  // the one the user picked (deep-review finding: LID removals key-collision).
  function setPollutant(index: number, pollutant: string) {
    onChange(removals.map((r, i) => (i === index ? { ...r, pollutant } : r)));
  }

  function setPercent(index: number, percent: number) {
    onChange(removals.map((r, i) => (i === index ? { ...r, percent } : r)));
  }

  function removeRow(index: number) {
    onChange(removals.filter((_, i) => i !== index));
  }

  if (pollutants.length === 0) return null;

  return (
    <section className="property-panel-group">
      <h4 className="named-collection-subsection-title">{t("lid.layerRemovals")}</h4>
      {removals.length === 0 && <p className="property-panel-hint">{t("lid.noRemovals")}</p>}
      {removals.length > 0 && (
        <div className="named-collection-points-table-wrap">
          <table className="named-collection-points-table">
            <thead>
              <tr>
                <th>{t("projectData.pollutant")}</th>
                <th>{t("lid.removalPercent")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {removals.map((removal, index) => (
                <tr key={index}>
                  <td>
                    <select
                      value={removal.pollutant}
                      onChange={(e) => setPollutant(index, e.target.value)}
                    >
                      <option value={removal.pollutant}>{removal.pollutant}</option>
                      {availableToAdd.map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <NumberInput value={removal.percent} onChange={(v) => setPercent(index, v)} />
                  </td>
                  <td>
                    <IconButton
                      icon={<TrashIcon />}
                      label={t("lid.deleteRemoval")}
                      onClick={() => removeRow(index)}
                      danger
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button className="button" onClick={addRow} disabled={availableToAdd.length === 0}>
        <PlusIcon />
        {t("lid.addRemoval")}
      </button>
    </section>
  );
}
